// Which BuildVizer surface this bundle is serving.
//
// Each persona family gets its own S3 bucket + domain, and the backend needs to know
// which one a request came from so it can answer "here are the personas you can use
// HERE" and "that persona lives somewhere else, go there".
//
// The value is RUNTIME config (`window.env`, generated per-bucket from that bucket's
// SSM prefix at deploy time), not a build-time constant — so one build artefact still
// serves all three domains. See src/utils/env.js for the two-tier lookup.

import { env } from "../utils/env";

export const APP_IDS = {
  WEB_APP: "WEB_APP", // app.buildvizer.com — developer / staff / Zumale admin
  WEB_INTERIOR: "WEB_INTERIOR", // interior.buildvizer.com — designers + BV associates
  WEB_ARCH: "WEB_ARCH", // arch.buildvizer.com — architects + freelancers
  WEB_VENDOR: "WEB_VENDOR", // vendor.buildvizer.com — execution vendors (v4_6)
  // Mobile-only surfaces. Listed so PERSONA_APP can name where a persona lives even
  // though this bundle is never one of them.
  MOBILE_BUYER: "MOBILE_BUYER",
  MOBILE_PARTNER: "MOBILE_PARTNER",
};

// The domain IS the identity, so hostname is a legitimate resolution — not a guess.
// This is the fallback when env.js is missing or empty (a failed deploy step, a local
// build, a preview host), and it degrades to something correct rather than to "".
// Ordered most-specific first.
const HOST_TO_APP_ID = [
  [/^interior\./i, APP_IDS.WEB_INTERIOR],
  [/^arch\./i, APP_IDS.WEB_ARCH],
  [/^vendor\./i, APP_IDS.WEB_VENDOR],
];

// Local development hosts. Everything here is "one machine running the whole app",
// which is not one of the three production surfaces.
const LOCAL_HOSTS = /^(localhost|127\.0\.0\.1|\[?::1\]?|.*\.local)$/i;
// LAN addresses too, so a phone or a second machine pointed at the dev server behaves
// the same way as the dev machine itself.
const LAN_HOST = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;

const isLocalHost = (host) => LOCAL_HOSTS.test(host) || LAN_HOST.test(host);

/**
 * Which surface this bundle is serving, or "" when it cannot be determined.
 *
 * "" is meaningful, not a failure: the backend treats a missing app id as "no
 * filtering" and offers every persona the account holds. That is exactly what local
 * development wants — one dev server standing in for all three domains, where being
 * silently locked out of six of the nine personas would be baffling.
 *
 * To exercise the real split locally, set REACT_APP_APP_ID (see the start:* scripts)
 * and the gate behaves precisely as it does in production.
 */
export const getAppId = () => {
  const fromEnv = env("REACT_APP_APP_ID");
  if (fromEnv) return fromEnv;

  const host = typeof window !== "undefined" ? window.location.hostname : "";

  const match = HOST_TO_APP_ID.find(([pattern]) => pattern.test(host));
  if (match) return match[1];

  // Unset on a dev host -> no app id at all, so nothing is filtered.
  if (isLocalHost(host)) return "";

  // A real deployed host that is not interior./arch. is the main app. This is the
  // fallback that keeps the site working if env.js failed to generate.
  return APP_IDS.WEB_APP;
};

// Public origin per surface, so a cross-domain row can link somewhere real. Runtime
// config again, so staging/preview domains work without a rebuild; falls back to the
// production hostnames.
const DEFAULT_ORIGINS = {
  [APP_IDS.WEB_APP]: "https://app.buildvizer.com",
  [APP_IDS.WEB_INTERIOR]: "https://interior.buildvizer.com",
  [APP_IDS.WEB_ARCH]: "https://arch.buildvizer.com",
  // Was absent, so originForApp(WEB_VENDOR) returned "" and any cross-domain row pointing a
  // vendor at their own portal rendered a dead link.
  [APP_IDS.WEB_VENDOR]: "https://vendor.buildvizer.com",
};

export const originForApp = (appId) =>
  env(`REACT_APP_ORIGIN_${appId}`) || DEFAULT_ORIGINS[appId] || "";
